import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getResourceById } from "../services/api";
import Sidebar    from "../components/Sidebar";
import ChatWidget from "../components/ChatWidget";
import PageHero   from "../components/PageHero";

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("es-PE", {
    year: "numeric", month: "long", day: "numeric",
  });
}

const TYPE_META = {
  video:  { label: "Video",        icon: "🎥", color: "#c0392b", bg: "#fde8e8" },
  slides: { label: "Diapositivas", icon: "📊", color: "#2980b9", bg: "#eaf3fb" },
  pdf:    { label: "PDF",          icon: "📄", color: "#a07800", bg: "#fdf3dc" },
  link:   { label: "Link",         icon: "🔗", color: "#16a085", bg: "#e6f5f0" },
};

function VideoViewer({ url }) {
  let videoId = "";
  if (url.includes("youtube.com/watch?v="))   videoId = url.split("v=")[1]?.split("&")[0];
  else if (url.includes("youtu.be/"))          videoId = url.split("youtu.be/")[1]?.split("?")[0];
  else if (url.includes("youtube.com/embed/")) videoId = url.split("embed/")[1]?.split("?")[0];

  if (!videoId) return (
    <div style={{ padding: "2rem", textAlign: "center" }}>
      <p style={{ color: "var(--text-muted)", marginBottom: "1rem", fontSize: "0.85rem" }}>
        No se pudo cargar el video.
      </p>
      <a href={url} target="_blank" rel="noreferrer" className="btn-primary"
        style={{ textDecoration: "none", padding: "8px 20px" }}>
        Ver en YouTube ↗
      </a>
    </div>
  );

  return (
    <div style={{ position: "relative", paddingBottom: "56.25%", height: 0, borderRadius: 10, overflow: "hidden", background: "#000" }}>
      <iframe
        style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: "none", maxWidth: "none" }}
        src={`https://www.youtube.com/embed/${videoId}`}
        title="Video"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}

function PdfViewer({ url }) {
  const [fail, setFail] = useState(false);
  return fail ? (
    <div style={{ padding: "2rem", textAlign: "center", background: "#f5f5f3", borderRadius: 8 }}>
      <p style={{ color: "var(--text-muted)", marginBottom: "1rem", fontSize: "0.85rem" }}>
        Tu navegador no puede mostrar el PDF aquí.
      </p>
      <a href={url} target="_blank" rel="noreferrer" className="btn-primary"
        style={{ textDecoration: "none", padding: "8px 20px", maxWidth: "none" }}>
        📄 Abrir PDF en nueva pestaña
      </a>
    </div>
  ) : (
    <iframe
      src={url}
      style={{ width: "100%", height: "80vh", minHeight: 480, border: "none", borderRadius: 8, display: "block", maxWidth: "none" }}
      title="Documento PDF"
      scrolling="yes"
      onError={() => setFail(true)}
    />
  );
}

function SlidesViewer({ url }) {
  const [fail, setFail] = useState(false);
  const isGoogleSlides  = url.includes("docs.google.com/presentation");

  let embedUrl;
  if (isGoogleSlides) {
    embedUrl = url.replace(/\/edit.*$/, "/embed").replace(/\/pub.*$/, "/embed");
    if (!embedUrl.includes("/embed")) embedUrl = url + "/embed";
  } else {
    embedUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;
  }

  if (fail) return (
    <div style={{ padding: "2rem", textAlign: "center", background: "#f5f5f3", borderRadius: 8 }}>
      <p style={{ color: "var(--text-muted)", marginBottom: "1rem", fontSize: "0.85rem" }}>
        No se pudo previsualizar el archivo.
      </p>
      <a href={url} target="_blank" rel="noreferrer"
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          background: "#2980b9", color: "#fff",
          fontFamily: "var(--font-display)", fontWeight: 700,
          fontSize: "0.82rem", padding: "8px 20px",
          borderRadius: 6, textDecoration: "none",
        }}>
        📥 Descargar presentación
      </a>
    </div>
  );

  return (
    <iframe
      src={embedUrl}
      style={{ width: "100%", height: "80vh", minHeight: 480, border: "none", borderRadius: 10, display: "block", maxWidth: "none" }}
      title="Diapositivas"
      allowFullScreen
      onError={() => setFail(true)}
    />
  );
}

function LinkViewer({ resource, meta }) {
  return (
    <div style={{
      background: "var(--white)", border: `2px solid ${meta.color}`,
      borderRadius: 10, padding: "2rem", textAlign: "center",
    }}>
      <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>{meta.icon}</div>
      <p style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "1rem", color: "var(--charcoal)", marginBottom: ".5rem" }}>
        {resource.title}
      </p>
      {resource.description && (
        <p style={{ fontSize: "0.85rem", color: "var(--text-muted)", marginBottom: "1.5rem", lineHeight: 1.6 }}>
          {resource.description}
        </p>
      )}
      <a href={resource.url} target="_blank" rel="noreferrer"
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          background: meta.color, color: "#fff",
          fontFamily: "var(--font-display)", fontWeight: 700,
          fontSize: "0.85rem", padding: "10px 24px",
          borderRadius: 6, textDecoration: "none",
        }}>
        Abrir enlace ↗
      </a>
    </div>
  );
}

export default function ResourceDetail() {
  const { id }                  = useParams();
  const navigate                = useNavigate();
  const [resource, setResource] = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  useEffect(() => {
    setLoading(true);
    getResourceById(id)
      .then(setResource)
      .catch(() => setError("No se pudo cargar el recurso."))
      .finally(() => setLoading(false));
  }, [id]);

  const meta = TYPE_META[resource?.type] || TYPE_META.link;

  const metadata = [
    resource?.type        && `Tipo: ${meta.label}`,
    resource?.author_name && `Autor: ${resource.author_name}`,
    resource?.created_at  && `Fecha: ${formatDate(resource.created_at)}`,
    resource?.url         && `URL: ${resource.url}`,
  ].filter(Boolean).join(" · ");

  const pdfUrl = resource?.type === "pdf" ? resource?.url : null;

  return (
    <div className="page">
      <PageHero
        section="recursos"
        title={loading ? "Cargando..." : (resource?.title ?? "Recurso")}
        backBtn={{ label: "Volver", onClick: () => navigate("/recursos") }}
      />

      <div className="two-col">
        <main>
          {error && <p style={{ color: "#c0392b" }}>{error}</p>}

          {loading && (
            <div className="skel">
              {[60, 35, 100, 90, 75].map((w, i) => (
                <div key={i} className="skel-line"
                  style={{ width: `${w}%`, height: i === 0 ? 18 : 12, marginBottom: 8 }} />
              ))}
            </div>
          )}

          {!loading && resource && (
            <div className="card">
              <div style={{
                display: "flex", alignItems: "flex-start", gap: "1rem",
                marginBottom: "1rem", paddingBottom: "1rem",
                borderBottom: "0.5px solid var(--border)",
              }}>
                <div style={{
                  width: 56, height: 56, borderRadius: 10, flexShrink: 0,
                  background: resource.thumbnail_url ? "transparent" : meta.bg,
                  overflow: "hidden", display: "flex", alignItems: "center",
                  justifyContent: "center", fontSize: "1.8rem",
                }}>
                  {resource.thumbnail_url
                    ? <img src={resource.thumbnail_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : meta.icon
                  }
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: ".6rem", flexWrap: "wrap", marginBottom: ".3rem" }}>
                    <h2 style={{
                      fontFamily: "var(--font-display)", fontWeight: 800,
                      fontSize: "2rem", color: "var(--teal-dark)",
                      margin: 0, lineHeight: 1.3,
                    }}>
                      {resource.title}
                    </h2>
                    <span style={{
                      fontSize: "0.65rem", fontFamily: "var(--font-display)", fontWeight: 700,
                      background: meta.bg, color: meta.color,
                      padding: "2px 10px", borderRadius: 20,
                      textTransform: "uppercase", letterSpacing: .3,
                      flexShrink: 0, whiteSpace: "nowrap",
                    }}>
                      {meta.label}
                    </span>
                  </div>
                  <div style={{ fontSize: "0.73rem", color: "var(--text-light)", display: "flex", gap: "1rem", flexWrap: "wrap" }}>
                    {resource.created_at && <span>Publicado el {formatDate(resource.created_at)}</span>}
                    {resource.author_name && <span>· {resource.author_name}</span>}
                  </div>
                </div>
                <a href={resource.url} target="_blank" rel="noreferrer"
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 4,
                    background: "var(--teal-light)", color: "var(--teal-dark)",
                    fontFamily: "var(--font-display)", fontWeight: 700,
                    fontSize: "0.72rem", padding: "5px 12px",
                    borderRadius: 20, textDecoration: "none",
                    flexShrink: 0, whiteSpace: "nowrap",
                  }}>
                  Abrir ↗
                </a>
              </div>

              {resource.description && (
                <p style={{ fontSize: "0.88rem", color: "var(--text-muted)", lineHeight: 1.7, marginBottom: "1.2rem" }}>
                  {resource.description}
                </p>
              )}

              <div style={{ borderRadius: 10, border: "0.5px solid var(--border)" }}>
                {resource.type === "video"  && <VideoViewer  url={resource.url} />}
                {resource.type === "pdf"    && <PdfViewer    url={resource.url} />}
                {resource.type === "slides" && <SlidesViewer url={resource.url} />}
                {resource.type === "link"   && <LinkViewer   resource={resource} meta={meta} />}
              </div>
            </div>
          )}
        </main>

        <Sidebar />
      </div>

      {!loading && resource && (
        <ChatWidget
          pageType="resource"
          title={resource.title ?? ""}
          content={resource.description ?? ""}
          pdfUrl={pdfUrl}
          metadata={metadata}
        />
      )}
    </div>
  );
}